import { useCallback, useEffect, useRef } from "react";
import { environment } from "../../environment";
import { useLogin } from "../login";
import { pluginRequest } from "./api";
import { BrandPluginConfig, BrandPluginConfigPatch, PluginApiResult, PluginPreviewData, PluginSnippet } from "./types";

// #region usePluginConfig
/**
 * The signed-in brand's plugin config (`/api/v2/plugin/*`). The config row is
 * created on first read, so GET always returns one.
 */
export const usePluginConfig = () => {
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);
    useEffect(() => { loginFunctionsRef.current = loginFunctions; }, [loginFunctions]);

    const logout = useCallback(() => loginFunctionsRef.current.logout(), []);
    const base = environment.pluginUrl;

    const getConfig = useCallback((): Promise<PluginApiResult<BrandPluginConfig>> =>
        pluginRequest<BrandPluginConfig>('GET', `${base}/config`, authToken, logout), [authToken, logout, base]);

    /** Each field sent replaces the stored value (theme and features are not merged server-side). */
    const updateConfig = useCallback((patch: BrandPluginConfigPatch): Promise<PluginApiResult<BrandPluginConfig>> =>
        pluginRequest<BrandPluginConfig>('PUT', `${base}/config`, authToken, logout, patch), [authToken, logout, base]);

    /** Issues a new public key; installed snippets with the old key stop working. */
    const rotateKey = useCallback((): Promise<PluginApiResult<BrandPluginConfig>> =>
        pluginRequest<BrandPluginConfig>('POST', `${base}/config/rotate-key`, authToken, logout), [authToken, logout, base]);

    const getSnippet = useCallback((): Promise<PluginApiResult<PluginSnippet>> =>
        pluginRequest<PluginSnippet>('GET', `${base}/snippet`, authToken, logout), [authToken, logout, base]);

    /** Live-preview payload for one of the brand's released Shopify products (skips domain/subscription gates). */
    const getPreviewData = useCallback((shopifyProductId: string): Promise<PluginApiResult<PluginPreviewData>> =>
        pluginRequest<PluginPreviewData>('GET', `${base}/config/preview/${encodeURIComponent(shopifyProductId)}`, authToken, logout),
    [authToken, logout, base]);

    return { getConfig, updateConfig, rotateKey, getSnippet, getPreviewData };
};
